/* eslint-disable complexity */
import { CancelWagerError, getClientErrorDetails } from '../../error/index.js';
import type { InteractionPostCancelMiddlewareFn } from '../../flow.js';
import { type OnInteraction } from '../../flow/types.js';
import { logger } from '../../utils/index.js';

/**
 * Middleware for handling cancel done interactions.
 *
 * @param onInteraction Callback function to handle the interaction when a cancel done is received
 * @param callbackOnDone Optional callback function to execute when the cancel action is done by the user
 * @param currencyFormatter Optional currency formatter function to format prices in the dialog messages
 * @returns Middleware function to be used in the cancel done flow
 */
export function cancelDoneMw(
  onInteraction: OnInteraction,
  callbackOnDone?: () => void,
  currencyFormatter?: (amount: number | string, options?: { shouldUseDecimals?: boolean; unit?: string }) => string
): ReturnType<InteractionPostCancelMiddlewareFn> {
  const fn: ReturnType<InteractionPostCancelMiddlewareFn> =
    ({ getCancelItems, getWagers }) =>
    async (next) => {
      try {
        let title = '';
        let message = '';

        const result = [];
        const error = [];
        const cancelItems = getCancelItems();

        const wagers = getWagers();
        for (const wager of wagers) {
          const value = cancelItems.get(wager);
          if (value && value.data) {
            result.push(value.data);
          }
          if (value && value.error) {
            error.push(value.error);
          }
        }

        const hasError = result.length === 0 && error.length > 0;

        if (result.length > 0) {
          const hasMarketplaceWager = result.find((wagerResponse) => {
            const responseTransactionId = wagerResponse.CancelWagerReply?.serial;
            return wagers.some((wager) => wager.transactionId === responseTransactionId && wager.isMarketplace);
          });

          if (hasMarketplaceWager) {
            title = 'Spelet är annullerat';
            message = 'Alla insatser har betalats tillbaka till lagets medlemmar.';
          } else {
            const totalCost: number = result.reduce((acc, wagerResponse) => {
              if (!wagerResponse.wagerAmounts?.totalAmount) {
                return acc;
              }
              return acc + Number.parseInt(wagerResponse.wagerAmounts.totalAmount, 10);
            }, 0);
            const gameString = result.length > 1 ? 'Dina' : 'Ditt';
            const formattedCost = currencyFormatter ? currencyFormatter(totalCost) : `${totalCost} kr`;
            title = `${gameString} spel för ${formattedCost} har annullerats`;
          }
          await onInteraction({ title, message });
          if (callbackOnDone) {
            callbackOnDone();
          }
        }

        if (hasError) {
          const [firstError] = error;
          if (firstError && firstError instanceof CancelWagerError) {
            const clientErrorObject = getClientErrorDetails(firstError.clientCode);
            title = clientErrorObject.title;

            message = clientErrorObject.message;
            await onInteraction({ title, message });
          }
        }
        next();
      } catch (error) {
        if (error instanceof Error) {
          logger.debug('Cancel done middleware error', { message: error.message, error });
        }
        next(error instanceof Error ? error : undefined);
      }
    };
  return fn;
}
